import { IVenueBasket } from "@app-contexts/dashboard/features/box-office/basket/interfaces/IVenueBasket";
import { IPublicBasket } from "@app-contexts/public/features/basket/interfaces/IPublicBasket";
import { enums } from "@shared/constants";
import {
  ItemTypesMapIdType,
  ShippingMethodsTypeMapIdType,
} from "@shared/constants/enums/enums-types";
import { useCallback, useMemo, useState } from "react";
import { VenueBasedTicketItemGroupSerializer } from "../types/common/ticket-item-group/serializer-types";
import { isProductPackageParent } from "../utils/helpers";

export function useBasketItemGroup(
  itemGroup: VenueBasedTicketItemGroupSerializer,
  basketService: IPublicBasket | IVenueBasket
) {
  const [quantity, setQuantity] = useState(itemGroup.quantity ?? 0);
  const { modifyItems, modifyItemGroups, removeItems, itemGroups } =
    basketService;

  const isDisabled = useMemo(
    () => itemGroup.quantity === quantity,
    [itemGroup, quantity]
  );

  const onRemove = useCallback(() => {
    const itemType = itemGroup.item_type;
    const isProductPackage = isProductPackageParent(
      itemGroup,
      basketService.itemGroups
    );

    // types that get consolidated and when pressing remove should remove all items
    const removeAllTypes: ItemTypesMapIdType[] = [
      enums.ITEM_TYPES_MAP.ITEM_TYPE_TICKET.id,
      enums.ITEM_TYPES_MAP.ITEM_TYPE_PRODUCT.id,
      enums.ITEM_TYPES_MAP.ITEM_TYPE_MEMBERSHIP.id,
    ];

    if ((itemType && !removeAllTypes.includes(itemType)) || isProductPackage) {
      return removeItems(itemGroup);
    }
    return modifyItems({
      item: itemGroup,
      quantity: 0,
    });
  }, [itemGroup, modifyItems, modifyItemGroups, removeItems]);

  const onUpdate = useCallback(
    () => modifyItems({ item: itemGroup, quantity }),
    [itemGroup, modifyItems, quantity]
  );

  const onUpdateItemGroupQuantity = useCallback(
    (quantity: number) => {
      let newQuantity = quantity;

      if (isProductPackageParent(itemGroup, itemGroups)) {
        const quantityInBasket = itemGroups.filter(
          (ig) => ig.type === itemGroup.type
        ).length;

        // UI here splits each quantity into 1 item group, so we need to subtract 1 for the current item group
        // to get the correct difference between the desired quantity and the current quantity in the basket
        // NEW QUANTITY = CURRENT TOTAL IN BASKET + ADDITIONAL QUANTITY
        // where additional quantity is the difference between the current item group quantity
        // and the desired quantity of that group
        newQuantity = quantityInBasket + (quantity - 1);
      }

      return modifyItems({ item: itemGroup, quantity: newQuantity });
    },
    [itemGroup, modifyItems, quantity, itemGroups]
  );

  const onUpdateItemGroupShippingType = useCallback(
    (shippingType: ShippingMethodsTypeMapIdType) =>
      modifyItemGroups([
        {
          itemGroupId: itemGroup.id,
          fieldsToUpdate: { shipping_type: shippingType },
        },
      ]),
    [itemGroup, modifyItemGroups]
  );

  // ticket packages: if item is a parent item, it should display. if it is a child, it should be hidden
  // the child items are displayed with the parent item
  const shouldDisplay = useMemo(
    () => Boolean(!itemGroup.parent),
    [itemGroup.parent]
  );

  return {
    isDisabled,
    quantity,
    setQuantity,
    onRemove,
    onUpdate,
    shouldDisplay,
    onUpdateItemGroupQuantity,
    onUpdateItemGroupShippingType,
  };
}
